import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import ElementHeightObserver from 'components/ElementHeightObserver';
import MobileLoginForm from 'components/forms/MobileLoginForm/MobileLoginForm';
import MobileAccountBackBtn from 'components/MobileAccountBackBtn';
import MobileLocaleTimezoneView from 'components/MobileLocaleTimezoneView/MobileLocaleTimezoneView';
import MobilePreferences from 'components/MobilePreferences/MobilePreferences';
import { asianViewMiddleSection, mobileAccountPagesBranding } from 'constants/branding';
import useMobilePreferences from 'hooks/useMobilePreferences';
import { getMobileSettingsLoginHeaderButtons } from 'redux/modules/appConfigs/selectors';
import { MobilePreferencesOption } from 'types/mobile';

import styles from './styles.module.scss';

const MobileLogin = () => {
  const { t } = useTranslation();

  const isLoginHeaderButtons = useSelector(getMobileSettingsLoginHeaderButtons);

  const [headerResizeElement, setHeaderResizeElement] = useState<HTMLElement | null>(null);

  const {
    showTimezoneView,
    showLocaleView,
    onSelectTimezone,
    onSelectLocal,
    setShowLocaleView,
    setShowTimezoneView,
    selectedOffset,
    selectedLocale,
    locales,
    timezoneOffsets
  } = useMobilePreferences();

  useEffect(() => {
    setHeaderResizeElement(document.getElementById('header-resize-container'));
  }, []);

  if ((showLocaleView || showTimezoneView) && selectedLocale && selectedOffset) {
    return (
      <MobileLocaleTimezoneView
        onGoBack={() => (showTimezoneView ? setShowTimezoneView(false) : setShowLocaleView(false))}
        onChangeOption={showLocaleView ? onSelectLocal : onSelectTimezone}
        label={showLocaleView ? t('locale.label') : t('timezone.label')}
        options={showLocaleView ? locales : (timezoneOffsets as MobilePreferencesOption[])}
        selectedOption={showLocaleView ? selectedLocale : (selectedOffset as MobilePreferencesOption)}
        type={showLocaleView ? 'locale' : 'timezone'}
        headerResizeElement={headerResizeElement}
      />
    );
  }

  const header = (
    <div
      className={classNames(
        asianViewMiddleSection.ACCOUNT_SUB_HEADER,
        mobileAccountPagesBranding.SUBHEADER,
        styles.header
      )}
    >
      <MobileAccountBackBtn
        className={styles.header__home}
        hideLabel
        iconName="biab_custom-icon-home"
        iconClassName={styles.header__home__icon}
      />
      <p className={styles.header__label}>{t('header.login')}</p>
      <div className={styles.header__end} />
    </div>
  );

  return (
    <>
      {headerResizeElement ? createPortal(header, headerResizeElement) : header}
      <ElementHeightObserver className={classNames('biab_login-container', styles.container)}>
        {isLoginHeaderButtons && (
          <div className={styles.mobileLoginFormContainer}>
            <MobileLoginForm />
          </div>
        )}
        <MobilePreferences
          setShowLocaleView={setShowLocaleView}
          setShowTimezoneView={setShowTimezoneView}
          selectedLocale={selectedLocale}
          selectedOffset={selectedOffset as MobilePreferencesOption}
          containerClassName={styles.preferences}
        />
      </ElementHeightObserver>
    </>
  );
};

export default MobileLogin;
